import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuthProvider } from '@auw/auth';
import {
  getTeamInviteLinkPreview,
  type TeamInviteLinkPreview,
} from '@auw/api-client';

interface Props {
  inviteToken: string;
}

export const InviteHomePage: React.FC<Props> = ({ inviteToken }) => {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<TeamInviteLinkPreview | null>(null);
  const [error, setError] = useState('');
  const googleProvider = useAuthProvider('google_oauth');

  useEffect(() => {
    let mounted = true;
    const loadPreview = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getTeamInviteLinkPreview(inviteToken);
        if (!mounted) return;
        setPreview(data);
      } catch (err) {
        if (!mounted) return;
        setPreview(null);
        setError(err instanceof Error ? err.message : '초대 정보를 불러올 수 없습니다.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void loadPreview();
    return () => {
      mounted = false;
    };
  }, [inviteToken]);

  const handleStartWithGoogle = () => {
    if (!googleProvider) return;
    const callback = `${window.location.origin}/auth/callback?teamInvite=${encodeURIComponent(inviteToken)}`;
    googleProvider.startLogin({ redirectUri: callback });
  };

  const inviterName = preview?.inviter_name?.trim() || '누군가';

  return (
    <Overlay>
      <Backdrop />
      <ModalCard>
        <Logo>askyour.ai</Logo>
        {loading ? (
          <Desc>초대 정보를 확인하는 중입니다...</Desc>
        ) : error ? (
          <>
            <Title>유효하지 않거나 만료된 초대 링크입니다</Title>
            <Desc>새 초대 링크를 요청한 뒤 다시 시도해 주세요.</Desc>
          </>
        ) : (
          <>
            <Title>{inviterName}님이 팀 워크스페이스에 초대했어요</Title>
            <Desc>[{preview?.team_name}] 팀에 참여해 함께 작업을 시작해보세요.</Desc>
          </>
        )}

        <GoogleButton onClick={handleStartWithGoogle} disabled={loading || !!error}>
          <GoogleLogo />
          Google로 시작하기
        </GoogleButton>
      </ModalCard>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.12);
`;

const ModalCard = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 460px;
  background: #fff;
  border-radius: 18px;
  padding: 32px 28px;
  box-shadow:
    0 12px 24px rgba(15, 23, 42, 0.08),
    0 2px 8px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Logo = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  line-height: 1.45;
  color: #111827;
`;

const Desc = styled.p`
  margin: 0 0 8px;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #6b7280;
`;

const GoogleButton = styled.button`
  margin-top: 8px;
  width: 100%;
  height: 50px;
  border-radius: 12px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #111827;
  font-size: 0.94rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #9ca3af;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GoogleLogo: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M47.532 24.553c0-1.632-.132-3.272-.414-4.877H24.48v9.242h12.963a11.089 11.089 0 0 1-4.804 7.28l-.044.291 6.972 5.404.483.048C43.96 38.14 47.532 31.87 47.532 24.553Z"
      fill="#4285F4"
    />
    <path
      d="M24.48 48c6.48 0 11.93-2.148 15.9-5.84l-7.41-5.742c-2.088 1.404-4.776 2.22-8.49 2.22-6.456 0-11.94-4.356-13.896-10.356l-.276.023-7.248 5.612-.095.264C7.236 42.588 15.24 48 24.48 48Z"
      fill="#34A853"
    />
    <path
      d="M10.584 28.282A14.768 14.768 0 0 1 9.78 24c0-1.5.252-2.952.78-4.282l-.014-.31-7.335-5.698-.24.114A24.022 24.022 0 0 0 .48 24c0 3.876.924 7.536 2.496 10.188l7.608-5.906Z"
      fill="#FBBC05"
    />
    <path
      d="M24.48 9.36c4.464 0 7.476 1.932 9.192 3.54l6.696-6.54C36.384 2.46 30.96 0 24.48 0 15.24 0 7.236 5.412 2.976 13.812L10.56 19.72c1.98-5.988 7.464-10.36 13.92-10.36Z"
      fill="#EB4335"
    />
  </svg>
);
